'use client'

import React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@workspace/ui/lib/utils'

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
    variants: {
        size: {
            xs: 'size-6',
            sm: 'size-7',
            md: 'size-8',
            lg: 'size-9',
            xl: 'size-12',
        },
    },
    defaultVariants: {
        size: 'md',
    },
})

const fallbackFontSizes: Record<NonNullable<VariantProps<typeof avatarVariants>['size']>, string> = {
    xs: 'text-[10px]',
    sm: 'text-xs',
    md: 'text-xs',
    lg: 'text-sm',
    xl: 'text-base',
}

type UserAvatarProps = VariantProps<typeof avatarVariants> & {
    src?: string | null
    alt?: string
    fallback?: string
    title?: string
    className?: string
    fallbackClassName?: string
}

function UserAvatar({ src, alt, fallback, title, size = 'md', className, fallbackClassName }: UserAvatarProps) {
    const displayTitle = title ?? alt ?? fallback

    return (
        <AvatarPrimitive.Root data-slot="avatar" className={cn(avatarVariants({ size }), className)}>
            <AvatarPrimitive.Image
                data-slot="avatar-image"
                src={src || undefined}
                alt={alt}
                title={displayTitle}
                className="aspect-square size-full object-cover"
            />
            <AvatarPrimitive.Fallback
                data-slot="avatar-fallback"
                className={cn(
                    'bg-sky-500 flex size-full items-center justify-center rounded-full text-white',
                    fallbackFontSizes[size ?? 'md'],
                    fallbackClassName,
                )}
                title={displayTitle}
            >
                {fallback?.charAt(0).toUpperCase()}
            </AvatarPrimitive.Fallback>
        </AvatarPrimitive.Root>
    )
}

export { UserAvatar, avatarVariants, type UserAvatarProps }
